/**
 * 📨 Postacı — Bildirim Sağlayıcısı
 * Global bildirim state'i, Supabase Realtime subscription, FCM entegrasyonu.
 *
 * Referans: Global Hedef Web Platform NotificationContext.tsx
 * Adaptasyon: Günüm Var DB şemasına uygun (body/data alanları, bildirim tipleri)
 */
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import type { Notification } from '@/types/database.types'
import { useToast } from '@/components/providers/ToastProvider'
import { TOAST_TITLES, TOAST_MESSAGES } from '@/constants/toastMessages'
import { Abacus } from '@/utils/abacus'
import {
  requestNotificationPermission,
  onForegroundMessage,
  onNotificationActionPerformed,
} from '@/services/fcmService'
import {
  fetchNotifications as fetchNotificationsFromDB,
  markAsRead as markAsReadDB,
  markAsUnread as markAsUnreadDB,
  markAllAsRead as markAllAsReadDB,
  deleteNotification as deleteNotificationDB,
  deleteReadNotifications as deleteReadNotificationsDB,
} from '@/services/notificationService'
import { drawRealtimeManager } from '@/services/realtime/DrawRealtimeManager'
import { SUPPRESSED_NOTIFICATION_SLUGS } from '@/services/realtime/types'
import { setSentryUser, clearSentryUser, setSentryTag } from '@/lib/sentry'
import Analytics from '@/lib/analytics'
import { initRevenueCat } from '@/lib/revenuecat'
import { Capacitor } from '@capacitor/core'
import { FirebaseMessaging } from '@capacitor-firebase/messaging'
import { getCachedSession } from '@/lib/sessionCache'
import { logError, logWarn } from '@/lib/log'
import { countUserDevices } from '@/services/notificationService'

// ============================================
// TYPES
// ============================================

interface NotificationContextType {
  notifications: Notification[]
  unreadCount: number
  loading: boolean
  permission: NotificationPermission
  hasDeviceToken: boolean
  markAsRead: (id: string) => Promise<void>
  markAsUnread: (id: string) => Promise<void>
  markAllAsRead: () => Promise<void>
  deleteNotification: (id: string) => Promise<void>
  clearReadNotifications: () => Promise<void>
  refreshNotifications: () => Promise<void>
  enableNotifications: (silent?: boolean) => Promise<void>
  retryTokenRegistration: () => Promise<void>
}

// ============================================
// CONTEXT
// ============================================

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

export function useNotifications(): NotificationContextType {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  return context
}

// ============================================
// TOAST TYPE MAPPER
// ============================================

type ToastMethod = (title: string, message: string) => void

function getToastMethodForType(
  type: string | null,
  toast: {
    showSuccess: ToastMethod
    showWarning: ToastMethod
    showError: ToastMethod
    showInfo: ToastMethod
  }
): ToastMethod {
  switch (type) {
    case 'payment_received':
    case 'rank_up':
      return toast.showSuccess
    case 'payment_reminder':
    case 'payment_reminder_payer':
      return toast.showWarning
    case 'payment_reminder_receiver':
      return toast.showSuccess
    case 'lottery_result':
      return toast.showSuccess
    case 'system_update':
      return () => {
        /* toast.showSuccess(title, message) -- Sayfa yenileme sürecinde toast kargaşasını önlemek için susturuldu */
      }
    default:
      return () => {
        /* toast.showInfo(title, message) -- Mükerrerliği önlemek için susturuldu */
      }
  }
}

// ============================================
// GLOBAL GUARD & INSTANCE TRACKING
// ============================================
const processedIds = new Set<string>()
const lastShownMessage = { text: '', time: 0 } // Content-based duplicate guard
const globalInstanceId = Math.random().toString(36).substring(7)

// ============================================
// PROVIDER
// ============================================

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>(() => {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      try {
        const persisted = localStorage.getItem('snn_notifications_cache')
        return persisted ? JSON.parse(persisted) : []
      } catch (_e) {
        return []
      }
    }
    return []
  })
  const [unreadCount, setUnreadCount] = useState(() => {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      try {
        const persisted = localStorage.getItem('snn_notifications_cache')
        if (persisted) {
          const list = JSON.parse(persisted) as Notification[]
          return list.filter(n => !n.is_read).length
        }
      } catch (_e) {}
    }
    return 0
  })
  const [loading, setLoading] = useState(() => {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      return !localStorage.getItem('snn_notifications_cache')
    }
    return true
  })
  const [userId, setUserId] = useState<string | null>(null)
  const [permission, setPermission] = useState<NotificationPermission>('default')
  const [hasDeviceToken, setHasDeviceToken] = useState(true) // Default true to avoid flash
  const toast = useToast()
  const navigate = useNavigate()

  const [isTokenChecked, setIsTokenChecked] = useState(false)
  const lastStartupUid = useRef<string | null>(null)
  const hasLoadedOnceRef = useRef(false)

  // Check initial permission
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      FirebaseMessaging.checkPermissions().then((status) => {
        setPermission(status.receive as NotificationPermission)
      }).catch(() => {})
    } else if ('Notification' in window) {
      setPermission(Notification.permission)
    }
  }, [])


  // Check if device token exists in DB
  const checkDeviceToken = useCallback(async () => {
    if (!userId) return
    try {
      setHasDeviceToken((await countUserDevices(userId)) > 0)
    } catch {
      // Sessiz hata
    } finally {
      setIsTokenChecked(true)
    }
  }, [userId])

  useEffect(() => {
    if (userId) {
      // ⚡ Defer device token check by 4 seconds to avoid blocking cold start
      const timer = setTimeout(() => {
        checkDeviceToken()
      }, 4000)
      return () => clearTimeout(timer)
    }
  }, [userId, checkDeviceToken])

  // Auto-Registration Recovery: 
  // If the user logged back in, the OS has permission granted, but the DB token is missing.
  useEffect(() => {
    if (userId && isTokenChecked && permission === 'granted' && !hasDeviceToken) {
      requestNotificationPermission(userId).then(success => {
        if(success) setHasDeviceToken(true)
      }).catch(err => logError('[Postacı] Auto-register hatası:', err))
    }
  }, [userId, isTokenChecked, permission, hasDeviceToken])

  // Track auth state
  useEffect(() => {
    const runStartupChecks = (uid: string) => {
      if (lastStartupUid.current === uid) return
      lastStartupUid.current = uid
      setTimeout(() => {
        initRevenueCat(uid)
      }, 3000)
    }

    // 🔐 Use cached session instead of making direct network request via getUser()
    getCachedSession().then(async ({ user }) => {
      const uid = user?.id ?? null
      setUserId(uid)
      if (uid) {
        setSentryUser(uid)
        setSentryTag('platform', Capacitor.getPlatform())
        Analytics.identifyUser(uid)
        runStartupChecks(uid)
      }
    })

    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      const uid = session?.user?.id ?? null
      setUserId(uid)
      if (uid) {
        setSentryUser(uid)
        setSentryTag('platform', Capacitor.getPlatform())
        Analytics.identifyUser(uid)
        runStartupChecks(uid)
      } else {
        clearSentryUser()
        lastStartupUid.current = null
      }
    })

    return () => {
      authListener.subscription.unsubscribe()
    }
  }, [])

  // Invite Reminders Check
  useEffect(() => {
    if (!userId) return

    const checkReminders = () => {
      const keys = Object.keys(localStorage).filter(k => k.startsWith('invite_remind_'))
      const now = Date.now()
      
      keys.forEach(key => {
        try {
          const data = JSON.parse(localStorage.getItem(key) || '{}')
          if (!data.remindAt || !data.deadlineAt) return
          
          if (now > new Date(data.deadlineAt).getTime()) {
            localStorage.removeItem(key) // Süresi geçmiş davet
          } else if (now > data.remindAt) {
            toast.showWarning(
              TOAST_TITLES.REMINDER, 
              `"${data.groupName}" grubu davetine yanıt verme süreniz azalıyor.`
            )
            // Bir daha baş ağrıtmasın, hatırlatıcıyı temizle
            localStorage.removeItem(key)
          }
        } catch(e) {
          logError('Reminder parse error:', e)
        }
      })
    }

    checkReminders()
    const timer = setInterval(checkReminders, 60000) // Her dakika kontrol
    return () => clearInterval(timer)
  }, [userId, toast])

  // 💾 Cache Sync Effect: notifications state'i değiştikçe localStorage'ı güncelle
  useEffect(() => {
    if (userId && !loading) {
      try {
        localStorage.setItem('snn_notifications_cache', JSON.stringify(notifications))
      } catch (e) {
        logWarn('[Postacı] Notifications cache write error:', e)
      }
    }
  }, [notifications, userId, loading])

  // Fetch notifications from DB
  const loadNotifications = useCallback(async () => {
    if (!userId) return

    try {
      if (!hasLoadedOnceRef.current) {
        setLoading(true)
      }
      // Premium UX Delay: Minimum 100ms bekleme
      const [data, _] = await Promise.all([
        fetchNotificationsFromDB(userId),
        Abacus.premiumDelay(100),
      ])
      setNotifications(data)
      setUnreadCount(data.filter(n => !n.is_read).length)
    } catch (error) {
      logError('❌ [Postacı] Bildirimler yüklenemedi:', error)
      toast.showError(
        TOAST_TITLES.ERROR,
        TOAST_MESSAGES.FETCH_NOTIFS_ERROR
      )
    } finally {
      hasLoadedOnceRef.current = true
      setLoading(false)
    }
  }, [userId, toast])

  // Initial fetch & Realtime subscription
  useEffect(() => {
    if (!userId) {
      setNotifications([])
      setUnreadCount(0)
      setLoading(false)
      if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
        localStorage.removeItem('snn_notifications_cache')
      }
      return
    }

    if (notifications.length === 0) {
      setLoading(true)
    }
    loadNotifications()

    const subscription = supabase
      .channel(`notifications:${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        payload => {
          const { eventType, new: newRec, old: oldRec } = payload

          if (eventType === 'INSERT') {
            const newNotification = newRec as Notification
            setNotifications(prev => [newNotification, ...prev])
            setUnreadCount(prev => prev + 1)

            // Toast göster (Sadece yeni ve işlenmemiş bildirimler için)
            const messageKey = `${newNotification.title}:${newNotification.body}`
            const isRecentlyShown =
              lastShownMessage.text === messageKey && Date.now() - lastShownMessage.time < 2000

            if (!processedIds.has(newNotification.id) && !isRecentlyShown) {
              processedIds.add(newNotification.id)
              lastShownMessage.text = messageKey
              lastShownMessage.time = Date.now()

              // ─── Spoiler Önleme: Çekiliş animasyonu aktifken draw_result toast bastır ───
              const isCeremonyRelated = newNotification.type ? SUPPRESSED_NOTIFICATION_SLUGS.has(newNotification.type) : false
              if (isCeremonyRelated && drawRealtimeManager.getCeremonyActive()) {
                // Bildirim state'e eklendi (yukarıda setNotifications), toast gösterilmedi
                // Seremoni bittikten sonra kullanıcı bildirimler sayfasında görebilir
              } else {
                const showToast = getToastMethodForType(newNotification.type, toast)
                showToast(newNotification.title, newNotification.body)
              }
            } else {
            }

            // Kura Heyecanı 🎉
            if (newNotification.type === 'lottery_result') {
              import('canvas-confetti').then((module) => {
                const confetti = module.default
                confetti({
                  particleCount: 150,
                  spread: 70,
                  origin: { y: 0.6 },
                  colors: ['#D4AF37', '#F1C40F', '#B8922C', '#FFFFFF'],
                })
              })
            }
          } else if (eventType === 'UPDATE') {
            const updated = newRec as Notification
            setNotifications(prev => {
              const prevNotif = prev.find(n => n.id === updated.id)
              if (prevNotif) {
                // Okunmadı -> Okundu geçişi
                if (!prevNotif.is_read && updated.is_read) {
                  setUnreadCount(uPrev => Math.max(0, uPrev - 1))
                }
                // Okundu -> Okunmadı geçişi
                else if (prevNotif.is_read && !updated.is_read) {
                  setUnreadCount(uPrev => uPrev + 1)
                }
              }
              return prev.map(n => (n.id === updated.id ? updated : n))
            })
          } else if (eventType === 'DELETE') {
            const deletedId = oldRec.id
            setNotifications(prev => {
              const target = prev.find(n => n.id === deletedId)
              if (target && !target.is_read) {
                setUnreadCount(uPrev => Math.max(0, uPrev - 1))
              }
              return prev.filter(n => n.id !== deletedId)
            })
          }
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(subscription)
    }
  }, [userId, loadNotifications, toast])

  // Auto-sync FCM token if permission already granted
  // Optimizasyon: Son 1 saat içinde sync yapıldıysa tekrar yapma (~1.8s tasarruf)
  const FCM_SYNC_CACHE_KEY = 'gv_fcm_last_sync'
  const FCM_SYNC_TTL = 60 * 60 * 1000 // 1 saat
  const tokenSyncRef = useRef<boolean>(false)

  useEffect(() => {
    if (!userId || tokenSyncRef.current) return

    const runAutoSync = async () => {
      let isGranted = false

      if (Capacitor.isNativePlatform()) {
        // ⚡ Kritik düzeltme: Native'de Notification.permission API'si yok.
        // Capacitor FirebaseMessaging.checkPermissions() ile gerçek izin durumunu oku.
        try {
          const permStatus = await FirebaseMessaging.checkPermissions()
          isGranted = permStatus.receive === 'granted'
          if (isGranted) setPermission('granted')
        } catch {
          isGranted = false
        }
      } else {
        isGranted = 'Notification' in window && Notification.permission === 'granted'
      }

      if (!isGranted) return

      // Cache kontrolü: Son 1 saat içinde sync yapıldıysa skip et
      const lastSync = localStorage.getItem(FCM_SYNC_CACHE_KEY)
      if (lastSync) {
        const elapsed = Date.now() - parseInt(lastSync, 10)
        if (elapsed < FCM_SYNC_TTL) {
          setPermission('granted')
          return
        }
      }

      tokenSyncRef.current = true
      setPermission('granted')

      // Fire-and-forget with callback: token edinimi arka planda fcmService içinde yapılır
      requestNotificationPermission(userId, undefined, (saved) => {
        if (saved) {
          setHasDeviceToken(true)
          localStorage.setItem(FCM_SYNC_CACHE_KEY, Date.now().toString())
        } else {
          logWarn('⚠️ [Auto-sync] Token kaydedilemedi, UI güncellendi')
          checkDeviceToken() // DB'den kontrol et
        }
      })
        .catch(() => { /* sessiz hata */ })
        .finally(() => { tokenSyncRef.current = false })
    }

    runAutoSync()
  }, [userId])

  // Foreground message listener
  useEffect(() => {
    const unsubscribe = onForegroundMessage(payload => {
      // Sadece listeyi yenile, toast'u Realtime listener (postgres_changes) gösterecek.
      // Böylece hem mükerrer toast önlenir hem de tipe özel renkler (Success/Warning) korunur.
      loadNotifications()
    })

    return unsubscribe
  }, [loadNotifications])

  // Background/Killed notification tap listener (Native)
  useEffect(() => {
    const unsubscribe = onNotificationActionPerformed(data => {
      if (data && data.url) {
        let targetUrl = data.url
        // Optional prefix adjustment
        if (targetUrl.startsWith('gunumvar://')) {
          targetUrl = targetUrl.replace('gunumvar://', '')
        }
        if (!targetUrl.startsWith('/')) {
          targetUrl = '/' + targetUrl
        }
        navigate(targetUrl)
      } else {
        // Fallback or default
        navigate('/notifications')
      }
    })

    return unsubscribe
  }, [navigate])

  // ============================================
  // ACTIONS (Optimistic UI)
  // ============================================

  const markAsRead = useCallback(
    async (id: string) => {
      // Optimistic
      setNotifications(prev => prev.map(n => (n.id === id ? { ...n, is_read: true } : n)))
      setUnreadCount(prev => Math.max(0, prev - 1))

      try {
        await markAsReadDB(id)
      } catch {
        loadNotifications() // Rollback
      }
    },
    [loadNotifications]
  )

  const markAsUnread = useCallback(
    async (id: string) => {
      // Optimistic
      setNotifications(prev => prev.map(n => (n.id === id ? { ...n, is_read: false } : n)))
      setUnreadCount(prev => prev + 1)

      try {
        await markAsUnreadDB(id)
      } catch {
        loadNotifications() // Rollback
      }
    },
    [loadNotifications]
  )

  const markAllAsRead = useCallback(async () => {
    // Optimistic
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })))
    setUnreadCount(0)

    try {
      await markAllAsReadDB()
    } catch {
      loadNotifications() // Rollback
    }
  }, [loadNotifications])

  const deleteNotification = useCallback(
    async (id: string) => {
      const target = notifications.find(n => n.id === id)

      // Optimistic
      setNotifications(prev => prev.filter(n => n.id !== id))
      if (target && !target.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1))
      }

      try {
        await deleteNotificationDB(id)
      } catch {
        loadNotifications() // Rollback
      }
    },
    [notifications, loadNotifications]
  )

  const clearReadNotifications = useCallback(
    async () => {
      if (!userId) return

      // Optimistic
      setNotifications(prev => prev.filter(n => !n.is_read))

      try {
        await deleteReadNotificationsDB(userId)
      } catch {
        loadNotifications() // Rollback
      }
    },
    [userId, loadNotifications]
  )

  const enableNotifications = useCallback(async (silent = false) => {
    if (!userId) return

    // 🛡️ Tarayıcı Kapasite Kontrolü (Sadece Web için)
    if (!Capacitor.isNativePlatform() && !('Notification' in window)) {
      if (!silent) toast.showError(TOAST_TITLES.UNSUPPORTED, TOAST_MESSAGES.NOTIFS_NOT_SUPPORTED)
      return
    }

    // 🛡️ Version check reload guard
    sessionStorage.setItem('gv_notif_flow_active', 'true')

    try {
      // Fire-and-Forget with callback:
      const granted = await requestNotificationPermission(userId, undefined, (saved) => {
        if (saved) {
          setHasDeviceToken(true)
          checkDeviceToken() // DB'den doğrula
        } else {
          setHasDeviceToken(false)
          toast.showWarning(TOAST_TITLES.TOKEN_FAILED, TOAST_MESSAGES.PUSH_TOKEN_FAILED)
        }
      })

      if (granted) {
        setPermission('granted')
        Analytics.notificationPermissionGranted()
        if (!Capacitor.isNativePlatform()) {
          toast.showSuccess(TOAST_TITLES.NOTIF_ACTIVE, TOAST_MESSAGES.DEVICE_REG_SUCCESS)
        } else {
        }
      } else {
        // Native veya Web üzerinden reddedilme durumları provider state'inde tutulur
        const isDenied = Capacitor.isNativePlatform() ? permission === 'denied' : Notification.permission === 'denied'
        if (isDenied) {
          setPermission('denied')
          Analytics.notificationPermissionDenied()
          toast.showWarning(
            TOAST_TITLES.PERMISSION_DENIED,
            TOAST_MESSAGES.PERM_DENIED_DESC
          )
        } else {
          toast.showInfo(TOAST_TITLES.OPERATION_CANCELLED, TOAST_MESSAGES.PERM_NOT_APPROVED)
        }
      }
    } catch (error) {
      logError('[Notification Error]', error)
      const errorMessage = error instanceof Error ? error.message : String(error)
      toast.showError(TOAST_TITLES.ERROR, TOAST_MESSAGES.NOTIF_ENABLE_FAILED(errorMessage))
    } finally {
      sessionStorage.removeItem('gv_notif_flow_active')
    }
  }, [userId, toast, checkDeviceToken])

  // Token yeniden bağlama fonksiyonu (manuel veya toggle-on tetiklemesi)
  const retryTokenRegistration = useCallback(async () => {
    if (!userId) return

    // 🛡️ Version check reload guard
    sessionStorage.setItem('gv_notif_flow_active', 'true')

    try {
      const granted = await requestNotificationPermission(userId, undefined, (saved) => {
        if (saved) {
          setHasDeviceToken(true)
          toast.showSuccess(TOAST_TITLES.CONNECTION_RESTORED, TOAST_MESSAGES.PUSH_REFRESHED)
        } else {
          setHasDeviceToken(false)
          toast.showWarning(TOAST_TITLES.TOKEN_NOT_RECEIVED, TOAST_MESSAGES.NOTIF_RETRY_FAILED)
        }
      })

      if (!granted) {
        toast.showWarning(TOAST_TITLES.TOKEN_NOT_RECEIVED, TOAST_MESSAGES.NOTIF_RETRY_FAILED)
      }
    } catch (error) {
      logError('❌ [Postacı] Token yenileme hatası:', error)
      const errorMessage = error instanceof Error ? error.message : String(error)
      toast.showError(TOAST_TITLES.CONNECTION_ERROR, errorMessage)
    } finally {
      sessionStorage.removeItem('gv_notif_flow_active')
    }
  }, [userId, toast])

  // ============================================
  // RENDER
  // ============================================

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        permission,
        hasDeviceToken,
        markAsRead,
        markAsUnread,
        markAllAsRead,
        deleteNotification,
        clearReadNotifications,
        refreshNotifications: loadNotifications,
        enableNotifications,
        retryTokenRegistration,
      }}
    >
      {children}
    </NotificationContext.Provider>
  )
}

export default NotificationProvider
